/**
 * S-72 海域図（事前連絡・実績）。担当: G4（F-RPT-03）
 * kintone 版 `portal/outputOceanAreaMap/*` の再現：条件選択（事前連絡／実績）・隻数表示／色塗りのみ・期間・
 * 事業実施者（複数）・農林漁区番号で検索し、海域図に件数を表示、検索結果を一覧表示する。
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Map as MapIcon, Search, X } from 'lucide-react';
import { Button, DataTable, DateField, EmptyState, ErrorState, FormField, PageHeader, Panel, RadioGroup, StatusBadge, TextField, type Column } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters';
import { SeaAreaMap, SeaMapLegend } from '@/features/portal/components/SeaAreaMap';
import { formatNumber } from '@/lib/format';
import { parseIdList, useCoverage, type CoverageFilter, type CoverageRowDto, type CoverageSource } from './api';
import './sea-map.css';

const SOURCES: { value: CoverageSource; label: string }[] = [
  { value: '事前連絡', label: '事前連絡' },
  { value: '実績', label: '実績（速報から集計）' },
];
const DISPLAYS = [
  { value: 'count', label: '隻数表示する（色塗＋隻数表示）' },
  { value: 'color', label: '色塗りのみ' },
] as const;

const yn = (v: boolean | null) => (v == null ? '' : v ? '有' : '無');

/** 検索条件（URL クエリ） */
function useCoverageParams(defaults: { from: string; to: string }) {
  const [params, setParams] = useSearchParams();
  const filter: CoverageFilter = useMemo(
    () => ({
      source: params.get('source') === '実績' ? '実績' : '事前連絡',
      from: params.get('from') || defaults.from,
      to: params.get('to') || defaults.to,
      businessOperatorIds: parseIdList(params.get('ops')),
      zoneNos: parseIdList(params.get('zones')),
    }),
    [params, defaults.from, defaults.to],
  );
  const display = params.get('display') === 'color' ? 'color' : 'count';
  const commit = (f: CoverageFilter, d: string) => {
    const p = new URLSearchParams();
    p.set('source', f.source);
    p.set('from', f.from);
    p.set('to', f.to);
    if (f.businessOperatorIds.length) p.set('ops', f.businessOperatorIds.join(','));
    if (f.zoneNos.length) p.set('zones', f.zoneNos.join(','));
    p.set('display', d);
    setParams(p, { replace: true });
  };
  return { filter, display, commit };
}

export function SeaMapPage() {
  const user = useCurrentUser();
  const { year, fiscalYear, yearPath } = useFiscalYear();
  const { filter, display, commit } = useCoverageParams({ from: fiscalYear.startDate, to: fiscalYear.endDate });
  const [draft, setDraft] = useState(filter);
  const [draftDisplay, setDraftDisplay] = useState<string>(display);
  const [zoneText, setZoneText] = useState(filter.zoneNos.join(','));
  const [periodError, setPeriodError] = useState<string | null>(null);
  const { data, error, isFetching, refetch } = useCoverage(year, filter);

  const toggleZone = (z: number) => {
    const cur = parseIdList(zoneText);
    const next = cur.includes(z) ? cur.filter((x) => x !== z) : [...cur, z].sort((a, b) => a - b);
    setZoneText(next.join(','));
  };

  const onSearch = () => {
    if (draft.from && draft.to && draft.from > draft.to) {
      setPeriodError('期間開始日は期間終了日より前の日付を指定してください');
      return;
    }
    setPeriodError(null);
    commit({ ...draft, from: draft.from || fiscalYear.startDate, to: draft.to || fiscalYear.endDate, zoneNos: parseIdList(zoneText) }, draftDisplay);
  };

  const isAdvance = filter.source === '事前連絡';
  const columns: Column<CoverageRowDto>[] = [
    { key: 'no', header: 'No.', width: 52, render: (_r, i) => <span className="bs-num">{i + 1}</span> },
    { key: 'businessOperatorName', header: '事業実施者', sortable: true, width: 170 },
    { key: 'vesselName', header: '船名', sortable: true, width: 110 },
    { key: 'nameKana', header: 'ﾌﾘｶﾞﾅ', width: 110 },
    { key: 'ownerName', header: '所有者', width: 100 },
    { key: 'captainName', header: '船長', width: 100 },
    { key: 'registrationNo', header: '漁船登録番号', width: 120, render: (r) => <span className="bs-num">{r.registrationNo}</span> },
    { key: 'tonnage', header: 'ﾄﾝ数', numeric: true, width: 70 },
    { key: 'crewCount', header: isAdvance ? '事前連絡乗組員数' : '乗組員数', numeric: true, width: 84 },
    { key: 'phone', header: '船舶電話', width: 120, render: (r) => <span className="bs-num">{r.phone}</span> },
    ...(isAdvance ? [{ key: 'totalBoardingCount', header: '参加船名簿全乗船人員', numeric: true, width: 96 } as Column<CoverageRowDto>] : []),
    { key: 'departureDate', header: '出港日', sortable: true, width: 104, render: (r) => <span className="bs-num">{r.departureDate}</span> },
    { key: 'arrivalDate', header: '帰港日', sortable: true, width: 104, render: (r) => <span className="bs-num">{r.arrivalDate}</span> },
    ...(isAdvance
      ? ([
          { key: 'zoneNumbers', header: '実施海区 農林漁区番号', width: 130, render: (r) => <span className="bs-num">{r.zoneNumbers.replaceAll('.', '・')}</span> },
          { key: 'catchSurveyPlanned', header: '漁獲調査予定', width: 76, align: 'center', render: (r) => yn(r.catchSurveyPlanned) },
          { key: 'catchSalePlanned', header: '漁獲物販売予定', width: 76, align: 'center', render: (r) => yn(r.catchSalePlanned) },
        ] as Column<CoverageRowDto>[])
      : ([
          { key: 'actualWorkDays', header: '作業日数', numeric: true, width: 76 },
          { key: 'catchSurveyStatus', header: '漁場調査等', width: 80, align: 'center' },
          { key: 'zoneNumbers', header: '調査水域', width: 130, render: (r) => <span className="bs-num">{r.zoneNumbers.replaceAll('.', '・')}</span> },
        ] as Column<CoverageRowDto>[])),
    { key: 'note', header: '備考', width: 120 },
    {
      key: 'link',
      header: '',
      width: 92,
      render: (r) =>
        r.noticeId ? (
          <Link className="bs-btn bs-btn--sm" to={yearPath(`advance-notices/${r.noticeId}`)}>
            事前連絡
          </Link>
        ) : r.voyageReportId ? (
          <Link className="bs-btn bs-btn--sm" to={yearPath(`voyage-reports/${r.voyageReportId}`)}>
            速報
          </Link>
        ) : null,
    },
  ];

  const totalCount = data?.zoneCounts.reduce((s, z) => s + z.count, 0) ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="海域情報"
        title="海域図（事前連絡・実績）"
        description="事前連絡の実施海区、または速報の調査水域を農林漁区番号ごとに集計して海域図に表示します。"
      />
      <div className="bs-seamap-page">
        <Panel title="検索条件" className="bs-seamap-page__cond">
          <div className="bs-seamap-cond">
            <FormField label="条件選択">
              <RadioGroup name="source" value={draft.source} onChange={(v) => setDraft({ ...draft, source: v })} options={SOURCES} direction="column" />
            </FormField>
            <FormField label="表示">
              <RadioGroup name="display" value={draftDisplay} onChange={setDraftDisplay} options={DISPLAYS.map((d) => ({ ...d }))} direction="column" />
            </FormField>
            <FormField label="期間" required error={periodError ?? undefined}>
              <div className="bs-seamap-cond__period">
                <DateField aria-label="期間開始" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
                <span>〜</span>
                <DateField aria-label="期間終了" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
              </div>
            </FormField>
            <FormField label="事業実施者" required={user.organizationType === '漁協'} hint={user.organizationType === '漁協' ? '自組織のみ表示されます' : '未選択はすべての事業実施者'}>
              <BusinessOperatorSelect
                multiple
                fiscalYear={year}
                value={draft.businessOperatorIds}
                onChange={(v) => setDraft((d) => ({ ...d, businessOperatorIds: v }))}
              />
            </FormField>
            <FormField label="農林漁区番号" hint="カンマ区切りで入力、または海域図のマスをクリックして選択">
              <div className="bs-seamap-cond__zones">
                <TextField value={zoneText} onChange={(e) => setZoneText(e.target.value)} placeholder="例）520,530" />
                {zoneText && <Button size="sm" variant="ghost" icon={<X size={14} />} aria-label="番号をクリア" onClick={() => setZoneText('')} />}
              </div>
            </FormField>
            <div className="bs-seamap-cond__actions">
              <Button variant="primary" icon={<Search size={16} />} loading={isFetching} onClick={onSearch}>
                検索
              </Button>
            </div>
          </div>
        </Panel>

        <Panel
          title="海域図"
          className="bs-seamap-page__map"
          actions={
            data && (
              <span className="bs-seamap-page__stat">
                該当 <strong className="bs-num">{formatNumber(data.rows.length)}</strong> 件／延べ <strong className="bs-num">{formatNumber(totalCount)}</strong> 隻
              </span>
            )
          }
        >
          {error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : (
            <SeaAreaMap
              counts={data?.zoneCounts ?? []}
              tone="blue"
              showCounts={display === 'count'}
              selected={parseIdList(zoneText)}
              onCellClick={toggleZone}
              legend={
                <>
                  <SeaMapLegend tone="blue">{isAdvance ? '調査海域（出港〜帰港）' : '調査水域（期間内の調査日）'}</SeaMapLegend>
                  {display === 'count' && <span>数字：調査隻数（1 隻が複数海区を調査するため、合計は隻数と一致しません）</span>}
                  <span className="bs-seamap-page__hint">オレンジ枠：番号条件として選択中（クリックで切替）</span>
                </>
              }
            />
          )}
        </Panel>
      </div>

      <section className="bs-seamap-page__result" aria-label="検索結果一覧">
        <h2 className="bs-seamap-page__h2">
          検索結果一覧（{filter.source}）
          <StatusBadge tone="navy">
            {filter.from} 〜 {filter.to}
          </StatusBadge>
        </h2>
        <DataTable
          caption={`検索結果一覧（${filter.source}）`}
          columns={columns}
          rows={data?.rows ?? []}
          rowKey={(r) => r.key}
          loading={!data && isFetching}
          pagination={{ pageSize: 50 }}
          dense
          empty={<EmptyState compact icon={<MapIcon />} title="該当するデータは存在しませんでした" description="期間・事業実施者・農林漁区番号を変更して検索してください。" />}
        />
      </section>
    </>
  );
}
